import { Flag } from "lucide-react";
import type { CrewResponse, FlagCode, ShiftAdmin, ShiftStatus } from "../../../api/types";
import { cn } from "../../../components/ui";

export const SHIFT_STATUS_LABEL: Record<ShiftStatus, string> = {
  TENTATIVE: "Tentative",
  CONFIRMED: "Confirmed",
  CANCELLED: "Cancelled",
  COMPLETED: "Completed",
};

export const SHIFT_TONE: Record<ShiftStatus, string> = {
  TENTATIVE: "border-warn/50 bg-warn/10 text-warn",
  CONFIRMED: "border-emerald-500/50 bg-emerald-500/10 text-emerald-300",
  CANCELLED: "border-zinc-600 bg-zinc-800/40 text-zinc-500 line-through",
  COMPLETED: "border-sky-500/50 bg-sky-500/10 text-sky-300",
};

export const FLAG_LABEL: Record<FlagCode, string> = {
  MULTI_VENUE_NO_TIMES: "Multiple venues, no times",
  EMPLOYEE_DEACTIVATED: "Employee deactivated",
  VENUE_ARCHIVED: "Venue archived",
  VENUE_TIMEZONE_CHANGED: "Venue timezone changed",
  SHIFT_OVERLAP_AFTER_TZ_CHANGE: "Overlap after timezone change",
  ROLE_UNMAPPED: "Role not mapped to a job role",
};

export const ROLE_SUGGESTIONS = ["Photographer", "Assistant", "Lead"];

export function ShiftStatusPill({ status, testId }: { status: ShiftStatus; testId: string }) {
  return (
    <span data-testid={testId} className={cn("inline-flex whitespace-nowrap rounded-sm border px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wider", SHIFT_TONE[status])}>
      {SHIFT_STATUS_LABEL[status]}
    </span>
  );
}

export function AttentionIcon({ show, testId }: { show: boolean; testId: string }) {
  if (!show) return null;
  return <Flag data-testid={testId} aria-label="Needs attention" className="h-3.5 w-3.5 shrink-0 fill-signal text-signal" />;
}

interface Timed {
  start_label: string | null;
  end_label: string | null;
  ends_next_day: boolean;
  tz_abbr: string;
}

export function timeRange(s: Timed): string {
  if (!s.start_label) return `No set times (${s.tz_abbr})`;
  return `${s.start_label} – ${s.end_label}${s.ends_next_day ? " (next day)" : ""}`;
}

export function fmtWorkDate(d: string, opts: Intl.DateTimeFormatOptions = { weekday: "short", month: "short", day: "numeric", year: "numeric" }): string {
  return new Date(`${d}T12:00:00`).toLocaleDateString("en-US", opts);
}

export function isoDate(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

export function addDays(d: Date, n: number): Date {
  const x = new Date(d);
  x.setDate(x.getDate() + n);
  return x;
}

export function todayIn(tz: string): string {
  return new Intl.DateTimeFormat("en-CA", { timeZone: tz, year: "numeric", month: "2-digit", day: "2-digit" }).format(new Date());
}

export function lateLabel(s: ShiftAdmin): string | null {
  const n = s.late_notices?.length ? s.late_notices[s.late_notices.length - 1] : null;
  return n ? `Running late${n.minutes ? ` (${n.minutes} min)` : ""}` : null;
}

const RESPONSE_UI: Record<CrewResponse, [string, string]> = {
  PENDING: ["Awaiting reply", "border-sky-400/40 text-sky-300"],
  CONFIRMED: ["Crew confirmed", "border-emerald-400/40 text-emerald-300"],
  DECLINED: ["Needs reassignment", "border-signal/60 text-signal"],
};

export function CrewBadges({ s, testPrefix, compact }: { s: ShiftAdmin; testPrefix: string; compact?: boolean }) {
  const late = s.status !== "CANCELLED" ? lateLabel(s) : null;
  const r = s.employee_response && s.status !== "CANCELLED" ? RESPONSE_UI[s.employee_response] : null;
  if (!late && !r) return null;
  const cls = cn("inline-flex whitespace-nowrap rounded-sm border px-1 py-px font-semibold uppercase tracking-wider", compact ? "text-[9px]" : "text-[10px]");
  return (
    <span className="mt-0.5 flex flex-wrap gap-1">
      {late && <span data-testid={`${testPrefix}-late-${s.id}`} className={cn(cls, "border-warn/60 text-warn")}>{late}</span>}
      {r && (!compact || s.employee_response === "DECLINED") && <span data-testid={`${testPrefix}-response-${s.id}`} className={cn(cls, r[1])}>{r[0]}</span>}
    </span>
  );
}
